import React from 'react';
import { v4 as uuid } from 'uuid';
import objectHash from 'object-hash';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableStateSnapshot,
  DroppableProvided
} from 'react-beautiful-dnd';
import { DayOfWeekSelect, EmployeeSelect, LocationStableSelect, ReloadIconButton } from '../../elements';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import { extractApiErrorMessage, getAcceptedLocationStablesByFarmType, notifications } from '../../utils';
import { DayOfWeek, TaskLibraryListType } from '../../models/common';
import { useConfirmation } from '../../hooks/ConfirmationService';
import TaskLibraryToolbar from './components/TaskLibraryToolbar';
import TaskItemLibrary from './components/TaskItemLibrary';
import { copyTaskList, moveTaskList, removeTaskList, reorderTaskList } from './utils/taskLists';
import { LocationStable } from '../../models/locations';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import {
  HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse,
  HealthSystemDomainTaskLibraryCommandCreateTaskScheduleViewModel,
  HealthSystemDomainTaskLibraryResultTaskScheduleViewModel,
  HealthSystemDomainTaskLibraryResultTaskTemplateViewModel,
  HealthSystemDomainUsersResultUserViewModel
} from '../../api/HealthSystemApi';
import { format, startOfDay, isWithinInterval, parseISO } from 'date-fns';
import {
  computeTotalDurationFromTemplates,
  computeExpectedEndTime,
  computePerItemEndTimesFromTemplates,
  formatDuration
} from '../../utils/taskTimeHelper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip
} from '@mui/material';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import CreateProductionScheduleModal from './components/CreateProductionScheduleModal';

const TRASH_ID = 'TRASH';
const ITEMS_ID = 'ITEMS';
const MAX_LANES = 7;

const TaskManager = () => {
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();
  const confirmDialog = useConfirmation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const apiService = new ApiService();

  const [selectedLocationStable, setSelectedLocationStable] = React.useState(LocationStable.Gestation);
  const [selectedWeekCycle, setSelectedWeekCycle] = React.useState(1);
  const [employees, setEmployees] = React.useState<HealthSystemDomainUsersResultUserViewModel[]>([]);
  const [tasksLibrary, setTasksLibrary] = React.useState<HealthSystemDomainTaskLibraryResultTaskTemplateViewModel[]>(
    []
  );
  const [lists, setLists] = React.useState<Record<string, HealthSystemDomainTaskLibraryResultTaskScheduleViewModel>>(
    {}
  );
  const [initialHash, setInitialHash] = React.useState<string | null>(null);
  const [isChanged, setIsChanged] = React.useState(false);
  const [allSchedules, setAllSchedules] = React.useState<
    HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse[]
  >([]);
  const [selectedDate, setSelectedDate] = React.useState<Date>(startOfDay(new Date()));
  const [createScheduleOpen, setCreateScheduleOpen] = React.useState(false);
  const [scheduleMenuAnchor, setScheduleMenuAnchor] = React.useState<HTMLElement | null>(null);

  const getTotalCount = (): number => Object.keys(lists).length;

  const loadEmployees = async () => {
    apiService.api.users
      .usersGetUsersAll({})
      .then((result) => {
        setEmployees(result.data);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const loadTaskLibrary = async (name: string) => {
    apiService.api.taskList
      .taskListGetTaskLibrary({ Stables: [selectedLocationStable], Name: name })
      .then((result) => {
        // Injections first, then newest first. Drag-copy reads this array by index, so sort the state itself.
        const isInjection = (task: HealthSystemDomainTaskLibraryResultTaskTemplateViewModel) =>
          task.type === TaskLibraryListType.VaccinationRule;
        setTasksLibrary(
          [...result.data].sort(
            (one, two) => Number(isInjection(two)) - Number(isInjection(one)) || (one.created > two.created ? -1 : 1)
          )
        );
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const loadTaskLists = async () => {
    apiService.api.taskList
      .taskListGetTaskLibraryList({
        Stables: [selectedLocationStable],
        WeekCycle: selectedWeekCycle,
        Date: format(selectedDate, 'yyyy-MM-dd')
      })
      .then((result) => {
        setLists(result.data);
        /*
        // If no task lists exist, create a default one
        if (Object.values(result).length === 0) {
          addList();
        }
        */
        // Compute the initial hash when the data is first loaded
        const hash = objectHash(result.data);
        setInitialHash(hash);
        setIsChanged(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const getScheduleEndDate = (
    schedule: HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse
  ): Date | null => {
    return schedule.endDate ? parseISO(schedule.endDate) : null;
  };

  const findScheduleForDate = (
    schedules: HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse[],
    date: Date
  ) => {
    const target = startOfDay(date);
    return (
      schedules.find((s) => {
        const start = startOfDay(parseISO(s.startDate));
        const end = getScheduleEndDate(s);
        if (!end) return target >= start;
        return isWithinInterval(target, { start, end });
      }) ?? null
    );
  };

  const productionSchedule = React.useMemo(
    () => findScheduleForDate(allSchedules, selectedDate),
    [allSchedules, selectedDate]
  );

  const getScheduleTimeBanner = (): 'past' | 'future' | null => {
    if (!productionSchedule) return null;
    const today = startOfDay(new Date());
    const scheduleStart = startOfDay(parseISO(productionSchedule.startDate));
    const scheduleEnd = getScheduleEndDate(productionSchedule);
    if (scheduleEnd && scheduleEnd < today) return 'past';
    if (scheduleStart > today) return 'future';
    return null;
  };

  const loadAllSchedules = async () => {
    return apiService.api.taskList
      .taskListGetAllProductionSchedules()
      .then((result) => {
        setAllSchedules(result.data);
      })
      .catch(() => {
        setAllSchedules([]);
      });
  };

  // Whenever the lists change, compare the current hash with the initial hash
  React.useEffect(() => {
    if (initialHash !== null) {
      const currentHash = objectHash(lists); // Generate hash of the current lists
      setIsChanged(currentHash !== initialHash); // Set isChanged if hashes differ
    }
  }, [lists, initialHash]);

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => {
        await loadEmployees();
        await loadTaskLibrary('');
        await loadTaskLists();
        await loadAllSchedules();
      };

      setIsBusy(true);
      loadData().finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
    }
  }, [loaded, selectedLocationStable]);

  const refresh = () => {
    setLoaded(false); // Reload data
  };

  // Reload task lists when the selected date changes
  const isInitialMount = React.useRef(true);
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (loaded) {
      loadTaskLists();
    }
  }, [selectedDate]);

  const saveTaskList = () => {
    setIsBusy(true);

    apiService.api.taskList
      .taskListUpsertTaskLibraryList({ stable: selectedLocationStable, weekCycle: selectedWeekCycle, lists })
      .then(() => {
        notifications.customInfo(t('f-tasks-manager-add-success'), 200);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        refresh();
      });
  };

  // A central function responsible for saving data without refresh
  const saveTaskListStorage = (
    data: Record<string, HealthSystemDomainTaskLibraryCommandCreateTaskScheduleViewModel>
  ) => {
    setIsBusy(true);

    apiService.api.taskList
      .taskListUpsertTaskLibraryList({ stable: selectedLocationStable, weekCycle: selectedWeekCycle, lists: data })
      .catch((e) => {
        // The lanes already show the change, so a failed save has to say so or it reads as saved.
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const hasPermission = () => {
    return state.role !== UserRole.user;
  };

  const addList = () => {
    if (getTotalCount() >= MAX_LANES) {
      notifications.warning(t('f-tasks-manager-no-more-than', { count: MAX_LANES }));
      return;
    }

    const dayOfWeek = getNextDayOfWeek();
    const employeeId = getNextEmployeeId();

    setLists((prevState) => {
      const newId = uuid();
      const updatedLists = {
        ...prevState,
        [newId]: {
          id: newId,
          dayOfWeek: dayOfWeek,
          employeeId: employeeId,
          weekCycle: selectedWeekCycle,
          taskItems: [],
          stable: selectedLocationStable
        }
      };

      saveTaskListStorage(updatedLists);
      return updatedLists;
    });
  };

  const deleteList = (listId: string) => {
    const doDeleteAction = async () => {
      setLists((prevState) => {
        const updatedLists = { ...prevState };
        delete updatedLists[listId];

        saveTaskListStorage(updatedLists);
        return updatedLists;
      });
    };

    const list = lists[listId];
    if (list.taskItems.length > 0) {
      const employee = employees.find((item) => item.id === list.employeeId) ?? { name: t('f-com-none') };

      confirmDialog({
        dialogId: 'delete-tasks-list-dialog',
        title: t('f-tasks-manager-confirm-title'),
        message: t('f-tasks-manager-confirm-text', { employee: employee.name }),
        alertSeverity: 'warning'
      }).then(() => {
        doDeleteAction();
      });
    } else {
      doDeleteAction();
    }
  };

  const cloneList = (listId: string) => {
    if (getTotalCount() >= MAX_LANES) {
      notifications.warning(t('f-tasks-manager-no-more-than', { count: MAX_LANES }));
      return;
    }

    setLists((prevState) => {
      const source = prevState[listId];
      const newId = uuid();
      const updatedLists = {
        ...prevState,
        [newId]: {
          id: newId,
          dayOfWeek: source.dayOfWeek,
          employeeId: undefined,
          weekCycle: selectedWeekCycle,
          taskItems: source.taskItems.map((item) => ({ ...item, id: uuid() })),
          stable: source.stable
        }
      };

      saveTaskListStorage(updatedLists);
      return updatedLists;
    });
  };

  const filterList = (name: string) => {
    loadTaskLibrary(name);
  };

  const handleDayOfWeekChange = (day: DayOfWeek, listId: string) => {
    setLists((prevState) => {
      const updatedLists = {
        ...prevState,
        [listId]: {
          id: prevState[listId].id,
          dayOfWeek: day,
          employeeId: prevState[listId].employeeId,
          weekCycle: prevState[listId].weekCycle,
          taskItems: prevState[listId].taskItems,
          stable: prevState[listId].stable
        }
      };

      saveTaskListStorage(updatedLists);
      return updatedLists;
    });
  };

  const handleUserSelect = (employeeId: string, listId: string) => {
    setLists((prevState) => {
      const updatedLists = {
        ...prevState,
        [listId]: {
          id: prevState[listId].id,
          dayOfWeek: prevState[listId].dayOfWeek,
          employeeId: employeeId,
          weekCycle: prevState[listId].weekCycle,
          taskItems: prevState[listId].taskItems,
          stable: prevState[listId].stable
        }
      };

      saveTaskListStorage(updatedLists);
      return updatedLists;
    });
  };

  const handleStartOfWorkingDayChange = (time: string, listId: string) => {
    setLists((prevState) => {
      const updatedLists = {
        ...prevState,
        [listId]: {
          ...prevState[listId],
          startOfWorkingDay: time
        }
      };

      saveTaskListStorage(updatedLists);
      return updatedLists;
    });
  };

  const getNextDayOfWeek = (): DayOfWeek => {
    const existingDaysOfWeek = Object.values(lists)
      .map((list) => list.dayOfWeek)
      .filter((day) => day !== undefined && day !== null);

    if (existingDaysOfWeek.length !== 0) {
      const options = existingDaysOfWeek.filter((day) => !existingDaysOfWeek.includes(day) && !isNaN(day));

      if (options.length > 0) return options[0];
    }

    return DayOfWeek.Monday;
  };

  const getNextEmployeeId = (): string => {
    const selectedEmployeeIds = Object.values(lists)
      .map((item) => item.employeeId)
      .filter((id) => id !== undefined && id !== null);

    if (selectedEmployeeIds.length !== 0) {
      const options = employees.filter((item) => !selectedEmployeeIds.includes(item.id));
      if (options.length > 0) return options[0].id;
      else return '';
    }

    return employees[0]?.id;
  };

  const handleStableChange = (locationStable: number) => {
    setSelectedLocationStable(locationStable);
    refresh();
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    if (!hasPermission()) {
      notifications.warning(t('f-tasks-manager-no-permission'));
      return;
    }

    if (destination.droppableId === TRASH_ID) {
      if (source.droppableId === ITEMS_ID) {
        return;
      }

      setLists((prevState) => {
        const updatedLists = {
          ...prevState,
          [source.droppableId]: removeTaskList(prevState[source.droppableId], source)
        };

        saveTaskListStorage(updatedLists);
        return updatedLists;
      });

      return;
    }

    if (source.droppableId === ITEMS_ID) {
      setLists((prevState) => {
        const updatedLists = {
          ...prevState,
          [destination.droppableId]: copyTaskList(
            tasksLibrary,
            prevState[destination.droppableId],
            source,
            destination,
            selectedLocationStable
          )
        };

        saveTaskListStorage(updatedLists);
        return updatedLists;
      });
    } else if (source.droppableId === destination.droppableId) {
      setLists((prevState) => {
        const updatedLists = {
          ...prevState,
          [destination.droppableId]: reorderTaskList(prevState[source.droppableId], source.index, destination.index)
        };

        saveTaskListStorage(updatedLists);
        return updatedLists;
      });
    } else {
      setLists((prevState) => {
        const updatedLists = {
          ...prevState,
          ...moveTaskList(prevState[source.droppableId], prevState[destination.droppableId], source, destination)
        };

        saveTaskListStorage(updatedLists);
        return updatedLists;
      });
    }
  };

  const handleDeleteTask = (listId: string, taskId: string) => {
    setLists((prevState) => {
      const source = prevState[listId];
      const taskIndex = source.taskItems.findIndex((task) => task.id === taskId);

      // If task not found, return the state without modification
      if (taskIndex === -1) {
        return prevState;
      }

      const updatedLists = {
        ...prevState,
        [listId]: removeTaskList(source, { index: taskIndex })
      };

      saveTaskListStorage(updatedLists);
      return updatedLists;
    });
  };

  const renderLane = (listId: string, provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
    const list = lists[listId];
    const totalMinutes = computeTotalDurationFromTemplates(list.taskItems);
    const endTime = computeExpectedEndTime(list.startOfWorkingDay, totalMinutes);
    const perItemTimes = computePerItemEndTimesFromTemplates(list.startOfWorkingDay, list.taskItems);
    return (
      <section className="min-w-0 rounded-xl border border-slate-200 bg-slate-50">
        <div className="flex flex-col gap-4 rounded-t-xl border-b border-slate-200 bg-white p-4">
          <EmployeeSelect
            variant="outlined"
            allowNotSelected
            fullWidth
            employees={loaded ? employees : []}
            labelText={t('f-employee')}
            disabled={!hasPermission()}
            value={list.employeeId}
            onUserSelect={(id) => handleUserSelect(id, listId)}
          />
          <DayOfWeekSelect
            variant="outlined"
            fullWidth
            disabled={!hasPermission()}
            value={list.dayOfWeek}
            onChange={(day) => handleDayOfWeekChange(day, listId)}
            selectKey={listId}
          />
          <TextField
            type="time"
            label={t('f-tasks-manager-start-of-working-day')}
            value={list.startOfWorkingDay ?? ''}
            disabled={!hasPermission()}
            onChange={(e) => handleStartOfWorkingDayChange(e.target.value, listId)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
            size="small"
            variant="outlined"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              {t('f-task-workspace-task-count', { count: list.taskItems.length, defaultValue: '{{count}} tasks' })}
            </span>
            <span className="font-medium text-slate-700">{formatDuration(totalMinutes)}</span>
          </div>
          {endTime && (
            <div className="text-xs text-indigo-700">
              {t('f-tasks-expected-end-time')}: <strong>{endTime}</strong>
            </div>
          )}
        </div>
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={
            'min-h-[180px] p-3 ' + (snapshot.isDraggingOver ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-300' : '')
          }
        >
          {list.taskItems.map((item, index) => (
            <Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={!hasPermission()}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  style={provided.draggableProps.style as React.CSSProperties}
                  className={
                    'mb-2 flex items-start gap-2 rounded-lg border bg-white p-3 ' +
                    (snapshot.isDragging ? 'border-indigo-400 shadow-lg' : 'border-slate-200')
                  }
                >
                  <div {...provided.dragHandleProps} className="mt-0.5 shrink-0 text-slate-400">
                    <Icon className="!text-base/none">drag_indicator</Icon>
                  </div>
                  <TaskItemLibrary task={item} editDisabled expectedEndTime={perItemTimes[index]} />
                  {hasPermission() && (
                    <Tooltip title={t('f-com-remove')}>
                      <IconButton
                        size="small"
                        sx={V4_ICON_BUTTON_SMALL}
                        aria-label={t('f-com-remove') + ': ' + (item.name || t('f-com-injection'))}
                        onClick={() => handleDeleteTask(listId, item.id)}
                      >
                        <Icon className="!text-base/none">close</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}
            </Draggable>
          ))}
          {list.taskItems.length === 0 && (
            <div className="flex min-h-[150px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 text-center text-sm text-slate-500">
              <Icon className="!text-2xl/none text-slate-400">playlist_add</Icon>
              {t('f-tasks-manager-drop-task')}
            </div>
          )}
          {provided.placeholder}
        </div>
        {hasPermission() && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-2">
            <Tooltip title={t('f-com-remove')}>
              <IconButton
                aria-label={t('f-com-remove')}
                size="small"
                sx={V4_ICON_BUTTON_SMALL}
                onClick={() => deleteList(listId)}
              >
                <Icon className="!text-lg/none">delete_outline</Icon>
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              color="inherit"
              sx={V4_BUTTON_DEFAULT}
              startIcon={<Icon className="!text-base/none">content_copy</Icon>}
              onClick={() => cloneList(listId)}
              disabled={getTotalCount() >= MAX_LANES}
            >
              {t('f-com-clone')}
            </Button>
          </div>
        )}
      </section>
    );
  };

  const renderWeeksCycles = (weekCycle: number) => {
    const cycles = Array.from({ length: weekCycle }, (_, index) => index + 1);

    return (
      <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {cycles.map((cycle) => (
          <Button
            key={cycle}
            variant={cycle === selectedWeekCycle ? 'contained' : 'text'}
            color={cycle === selectedWeekCycle ? 'primary' : 'inherit'}
            sx={cycle === selectedWeekCycle ? undefined : V4_BUTTON_DEFAULT}
            aria-pressed={cycle === selectedWeekCycle}
            onClick={() => {
              setSelectedWeekCycle(cycle);
              refresh();
            }}
          >
            {cycle} {t('f-com-week')}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <Box sx={{ height: '100%' }}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="m-0 text-lg font-semibold text-slate-900">{t('f-tasks-manager')}</h2>
          <p className="mt-1 mb-0 text-sm text-slate-500">
            {t(
              'f-task-workspace-planning-help',
              'Choose a stable and week, then drag tasks into an employee’s schedule.'
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isBusy && <CircularProgress size={20} />}
          <ReloadIconButton action={refresh} />
          {hasPermission() && (
            <>
              <Button
                onClick={addList}
                id="addButton"
                disabled={isBusy || !productionSchedule || getTotalCount() >= MAX_LANES}
                startIcon={<Icon>add</Icon>}
                variant="outlined"
                color="primary"
              >
                {t('f-task-workspace-add-lane', 'Add assignment')}
              </Button>
              <Button
                onClick={saveTaskList}
                id="addSave"
                disabled={isBusy || !isChanged}
                startIcon={<Icon>save_outlined</Icon>}
                variant="contained"
                color="primary"
              >
                {t('f-com-save')}
              </Button>
              <Tooltip title={t('f-task-workspace-schedule-settings', 'Schedule settings')}>
                <span>
                  <IconButton
                    id="scheduleSettingsButton"
                    sx={V4_ICON_BUTTON}
                    aria-label={t('f-task-workspace-schedule-settings', 'Schedule settings')}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(scheduleMenuAnchor)}
                    aria-controls={scheduleMenuAnchor ? 'scheduleSettingsMenu' : undefined}
                    disabled={!loaded}
                    onClick={(event) => setScheduleMenuAnchor(event.currentTarget)}
                  >
                    <Icon>more_vert</Icon>
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {loaded && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 mb-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                format="dd/MM/yyyy"
                label={t('f-tasks-production-schedule-viewing-date')}
                value={selectedDate}
                onChange={(value) => {
                  if (value) setSelectedDate(startOfDay(value as Date));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    style: { minWidth: 220 }
                  }
                }}
              />
            </LocalizationProvider>

            {productionSchedule && (
              <>
                <div>
                  <span className="text-xs text-gray-500">{t('f-tasks-production-schedule-start-date')}</span>
                  <p className="font-semibold text-gray-800">{formatDate(productionSchedule.startDate)}</p>
                </div>
                {getScheduleEndDate(productionSchedule) && (
                  <div>
                    <span className="text-xs text-gray-500">{t('f-tasks-production-schedule-end-date')}</span>
                    <p className="font-semibold text-gray-800">{formatDate(productionSchedule.endDate!)}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {loaded &&
        (() => {
          const timeBanner = getScheduleTimeBanner();
          if (!timeBanner) return null;
          return (
            <div className="mb-2">
              <Alert severity="info">
                {timeBanner === 'past'
                  ? t('f-tasks-production-schedule-past-banner', {
                      endDate: formatDate(productionSchedule!.endDate!),
                      interpolation: { escapeValue: false }
                    })
                  : t('f-tasks-production-schedule-future-banner', {
                      startDate: formatDate(productionSchedule!.startDate),
                      interpolation: { escapeValue: false }
                    })}
              </Alert>
            </div>
          );
        })()}

      {loaded && !productionSchedule && (
        <div className="flex flex-col items-center justify-center gap-4 mt-12 p-8">
          <Icon style={{ fontSize: 64, color: '#9e9e9e' }}>{'calendar_today'}</Icon>
          <p className="text-gray-600 text-center max-w-md">{t('f-tasks-production-schedule-no-schedule-for-date')}</p>
        </div>
      )}

      {loaded && productionSchedule && (
        <>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div className="w-full sm:w-64">
              <LocationStableSelect
                value={selectedLocationStable}
                fullWidth
                onLocationStableChange={handleStableChange}
                includeOnly={getAcceptedLocationStablesByFarmType(state.farmType)}
              />
            </div>
            {renderWeeksCycles(productionSchedule.totalWeeks)}
          </div>
          <div className="grid min-w-0 grid-cols-1 items-start gap-5 lg:grid-cols-[250px_minmax(0,1fr)]">
            <DragDropContext onDragEnd={onDragEnd}>
              <aside className="box-border flex flex-col rounded-xl border border-slate-200 p-3 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)]">
                {hasPermission() && getTotalCount() != 0 && (
                  <Droppable droppableId={TRASH_ID}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={
                          'mb-4 rounded-lg border border-dashed px-3 py-3 text-center text-xs ' +
                          (snapshot.isDraggingOver
                            ? 'border-red-400 bg-red-50 text-red-700'
                            : 'border-slate-300 text-slate-500')
                        }
                      >
                        {!snapshot.isDraggingOver ? t('f-tasks-manager-delete-task') : ''}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}

                <div className="mb-3">
                  <TaskLibraryToolbar
                    disabled={!loaded || !hasPermission()}
                    onAddAction={() => filterList('')}
                    onSearchAction={filterList}
                  />
                </div>

                <Droppable droppableId={ITEMS_ID} isDropDisabled={true}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="-mr-2 max-h-[60vh] min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-2 lg:max-h-none"
                    >
                      {tasksLibrary && tasksLibrary.length > 0 ? (
                        tasksLibrary.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id}
                            index={index}
                            isDragDisabled={!hasPermission()}
                          >
                            {(provided, snapshot) => (
                              <React.Fragment>
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  // Cast needed: @radix-ui augments CSSProperties with a `--radix-*` index
                                  // signature that react-beautiful-dnd's DraggingStyle does not declare.
                                  style={provided.draggableProps.style as React.CSSProperties}
                                  className={
                                    'flex rounded-lg border bg-white px-2 py-1.5 ' +
                                    (snapshot.isDragging
                                      ? 'border-indigo-400 shadow-lg'
                                      : 'border-slate-200 hover:border-indigo-300')
                                  }
                                >
                                  <TaskItemLibrary
                                    key={item.id}
                                    task={item}
                                    compact
                                    editDisabled={item.type === TaskLibraryListType.VaccinationRule}
                                    onUpdateAction={() => filterList('')}
                                  />
                                </div>
                                {snapshot.isDragging && (
                                  <div className="flex rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                                    <TaskItemLibrary
                                      key={item.id}
                                      task={item}
                                      compact
                                      editDisabled={item.type === TaskLibraryListType.VaccinationRule}
                                    />
                                  </div>
                                )}
                              </React.Fragment>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <div className="p-6 text-center text-sm text-slate-500">{t('f-no-data')}</div>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </aside>
              <div className="min-w-0 pb-4" role="region" aria-label={t('f-tasks-planning')}>
                <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2 2xl:grid-cols-3">
                  {Object.keys(lists).map((listId) => (
                    <div key={listId} className="shrink-0">
                      <Droppable droppableId={listId} isDropDisabled={!hasPermission()}>
                        {(provided, snapshot) => renderLane(listId, provided, snapshot)}
                      </Droppable>
                    </div>
                  ))}
                  {getTotalCount() === 0 && (
                    <div className="flex min-h-[280px] w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-300 p-6 text-center">
                      <Icon className="!text-3xl/none text-slate-400">event_note</Icon>
                      <p className="m-0 text-sm text-slate-500">
                        {t('f-task-workspace-empty-board', 'No assignments for this stable and week.')}
                      </p>
                      {hasPermission() && (
                        <Button
                          onClick={addList}
                          disabled={isBusy}
                          color="primary"
                          variant="outlined"
                          startIcon={<Icon>add</Icon>}
                        >
                          {t('f-task-workspace-add-lane', 'Add assignment')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </DragDropContext>
          </div>
        </>
      )}

      <Menu
        id="scheduleSettingsMenu"
        anchorEl={scheduleMenuAnchor}
        open={Boolean(scheduleMenuAnchor)}
        onClose={() => setScheduleMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { 'aria-labelledby': 'scheduleSettingsButton' } }}
      >
        <MenuItem
          onClick={() => {
            setScheduleMenuAnchor(null);
            setCreateScheduleOpen(true);
          }}
        >
          {t('f-tasks-production-schedule-create')}
        </MenuItem>
      </Menu>

      <CreateProductionScheduleModal
        open={createScheduleOpen}
        currentSchedule={productionSchedule}
        onClose={() => setCreateScheduleOpen(false)}
        onCreated={() => {
          loadAllSchedules();
          setCreateScheduleOpen(false);
        }}
      />
    </Box>
  );
};

export default TaskManager;
